import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow, Chip } from "@/components/Reveal";
import CheckoutButton from "@/components/CheckoutButton";
import SamplePreviewButton from "@/components/SamplePreviewModal";
import { scrollToId } from "@/lib/scroll";

const PERSONAS = [
  {
    key: "assess",
    stage: "01 · Assess",
    quote: "I don't know what's wrong.",
    rec: "Start with the $9.99 Business Snapshot or go straight to the full $99.99 Assessment.",
    cta: "Get My Assessment — $99.99",
    kind: "checkout",
  },
  {
    key: "build",
    stage: "02 · Build",
    quote: "I know what I need built.",
    rec: "The Local Foundation package covers exactly this — or book a consultation for custom scope.",
    cta: "View Marketing Packages",
    kind: "packages",
  },
  {
    key: "grow",
    stage: "03 · Grow",
    quote: "I need more customers.",
    rec: "The Growth Engine package is built for this — ongoing local SEO, funnels and monthly KPI reports.",
    cta: "View Marketing Packages",
    kind: "packages",
  },
  {
    key: "scale",
    stage: "04 · Scale",
    quote: "I need automation, AI or custom systems.",
    rec: "Discuss a scale project — every advanced implementation begins with discovery.",
    cta: "Discuss a Scale Project",
    kind: "consulting",
  },
];

const STAGES = [
  {
    num: "01",
    title: "Assess",
    tagline: "Understand Before You Spend.",
    desc: "We evaluate your current position, digital assets, visibility, customer journey, marketing systems, and growth opportunities.",
    services: [
      "Google Business assessment",
      "Website review",
      "SEO review",
      "Competitor research",
      "Social-media audit",
      "Lead funnel review",
      "Analytics review",
      "Brand review",
      "Technology assessment",
    ],
    cta: "Assess My Business",
    testid: "system-cta-assess",
    span: "lg:col-span-7",
    accent: "text-gold",
    bar: "from-gold to-gold-bright",
  },
  {
    num: "02",
    title: "Build",
    tagline: "Build the Foundation.",
    desc: "Once the priorities are clear, we help establish the digital infrastructure your business needs.",
    services: [
      "Website design & development",
      "Landing pages",
      "Lead-generation funnels",
      "Brand development",
      "Business graphics",
      "Digital product creation",
      "Email marketing setup",
      "CRM setup",
      "Business email configuration",
      "Google Business optimization",
      "Social-media setup",
      "Analytics installation",
      "Conversion tracking",
      "E-commerce configuration",
    ],
    cta: "Request a Build Quote",
    testid: "system-cta-build",
    span: "lg:col-span-5",
    accent: "text-emerald-400",
    bar: "from-emerald-500 to-emerald-400",
  },
  {
    num: "03",
    title: "Grow",
    tagline: "Turn Your Foundation Into a Marketing Engine.",
    desc: "Attract, engage, and convert customers with campaigns scoped to your channels, content volume, and level of management required.",
    services: [
      "Search engine optimization",
      "Search engine marketing",
      "Social-media marketing",
      "Content marketing",
      "Short-form video campaigns",
      "Reels & promotional content",
      "PPC campaign management",
      "Email campaigns",
      "Lead nurturing",
      "Local marketing",
      "Reputation strategy",
      "Conversion optimization",
      "Marketing analytics",
      "Campaign reporting",
    ],
    cta: "Build My Growth Plan",
    testid: "system-cta-grow",
    span: "lg:col-span-5",
    accent: "text-emerald-400",
    bar: "from-emerald-500 to-emerald-400",
  },
  {
    num: "04",
    title: "Scale",
    tagline: "Automate What Works.",
    desc: "Combine marketing strategy with technology and automation. Every advanced implementation begins with discovery and defined requirements.",
    services: [
      "AI-assisted marketing workflows",
      "Automated lead intake",
      "CRM automation",
      "Customer onboarding automation",
      "Sales pipeline automation",
      "Executive dashboards",
      "Reporting systems",
      "Business intelligence",
      "API integrations",
      "Custom software",
      "AI assistants & agents",
      "Workflow automation",
      "Web3 integrations",
      "Custom business applications",
    ],
    cta: "Discuss a Scale Project",
    testid: "system-cta-scale",
    span: "lg:col-span-7",
    accent: "text-gold",
    bar: "from-gold to-gold-bright",
  },
];

export const GrowthSystem = () => {
  const [active, setActive] = useState("assess");
  const persona = PERSONAS.find((p) => p.key === active);

  return (
    <section id="system" className="relative py-24 sm:py-32 bg-obsidian-900/50 border-y border-slate-800/60 grain">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow>The Framework</Eyebrow>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mt-4">
            The All In Hand Growth System
          </h2>
          <p className="text-slate-400 mt-4 max-w-2xl">
            Instead of selling disconnected marketing services, All In Hand Marketing organizes
            business growth into four stages. Where are you right now?
          </p>
        </Reveal>

        <Reveal delay={0.08} className="mt-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stage-selector">
            {PERSONAS.map((p) => (
              <button
                key={p.key}
                data-testid={`stage-option-${p.key}`}
                onClick={() => setActive(p.key)}
                className={`rounded-2xl border p-4 sm:p-5 text-left transition-all duration-300 ${
                  active === p.key
                    ? "border-gold/70 bg-gold/10"
                    : "border-slate-700/60 bg-obsidian-800/50 hover:border-gold/40"
                }`}
              >
                <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${active === p.key ? "text-gold" : "text-slate-500"}`}>
                  {p.stage}
                </span>
                <p className="font-display font-semibold text-white text-sm sm:text-base mt-2 leading-snug">
                  “{p.quote}”
                </p>
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.12} className="mt-4">
          <div
            data-testid="stage-recommendation"
            className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between"
          >
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400">
                Recommended next step · {persona.stage}
              </p>
              <p className="text-slate-200 text-sm mt-1.5">{persona.rec}</p>
            </div>
            {persona.kind === "checkout" ? (
              <CheckoutButton
                lookup="aih_full_assessment"
                label={persona.cta}
                testid="stage-recommendation-cta"
                className="shrink-0 rounded-full bg-gold text-obsidian font-display font-bold px-6 py-3 text-sm hover:bg-gold-bright transition-colors"
              />
            ) : persona.kind === "packages" ? (
              <button
                data-testid="stage-recommendation-cta"
                onClick={() => scrollToId("packages")}
                className="shrink-0 text-center rounded-full bg-gold text-obsidian font-display font-bold px-6 py-3 text-sm hover:bg-gold-bright transition-colors"
              >
                {persona.cta}
              </button>
            ) : (
              <Link
                to="/intake?type=consultation"
                data-testid="stage-recommendation-cta"
                className="shrink-0 text-center rounded-full bg-gold text-obsidian font-display font-bold px-6 py-3 text-sm hover:bg-gold-bright transition-colors"
              >
                {persona.cta}
              </Link>
            )}
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mt-12">
          {STAGES.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.08} className={s.span}>
              <div
                className={`group relative rounded-3xl bg-obsidian-800/70 border p-8 sm:p-10 transition-all duration-500 hover:-translate-y-1 overflow-hidden h-full ${
                  active === ["", "assess", "build", "grow", "scale"][Number(s.num)]
                    ? "border-gold/50"
                    : "border-slate-700/60 hover:border-gold/40"
                }`}
              >
                <div
                  className={`absolute top-0 left-0 h-full w-1 bg-gradient-to-b ${s.bar} opacity-60 group-hover:opacity-100 transition-opacity`}
                />
                <div className="flex items-start justify-between gap-4">
                  <span className={`font-display text-5xl sm:text-6xl font-extrabold ${s.accent} opacity-90`}>
                    {s.num}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 pt-3">
                    Stage {s.num}
                  </span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mt-5">{s.title}</h3>
                <p className={`font-display font-semibold mt-1.5 ${s.accent}`}>{s.tagline}</p>
                <p className="text-slate-400 text-sm leading-relaxed mt-4">{s.desc}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-7 mb-3">
                  Services may include
                </p>
                <div className="flex flex-wrap gap-2">
                  {s.services.map((srv) => (
                    <Chip key={srv}>{srv}</Chip>
                  ))}
                </div>
                <button
                  data-testid={s.testid}
                  onClick={() => setActive(s.num === "01" ? "assess" : s.num === "02" ? "build" : s.num === "03" ? "grow" : "scale")}
                  className="mt-8 inline-flex items-center gap-2 font-display font-bold text-sm text-white hover:text-gold transition-colors"
                >
                  {s.cta}
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="text-slate-500 text-sm mt-8">
            Implementation packages are customized according to business requirements, project scope,
            technology, and deliverables.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default GrowthSystem;
